// src/components/vendor/VendorLogin.tsx
import React, { useState } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Link,
  Divider,
  Checkbox,
  FormControlLabel,
  Alert,
  CircularProgress,
  InputAdornment,
  IconButton
} from '@mui/material';
import { Visibility, VisibilityOff, Lock, Email } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom'; // Import useNavigate
import { useAuth } from '../../auth/AuthContext'; // Adjust the import path as necessary
 // Import useAuth (adjust path if necessary)

interface LoginFormData {
  email: string;
  password: string;
  rememberMe: boolean;
}

interface LoginError {
  email?: string;
  password?: string;
  general?: string;
}

const VendorLogin: React.FC = () => {
  const [formData, setFormData] = useState<LoginFormData>({
    email: '',
    password: '',
    rememberMe: false
  });
  
  const [errors, setErrors] = useState<LoginError>({});
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [loginError, setLoginError] = useState<string | null>(null);

  const navigate = useNavigate(); // Initialize navigate
  const auth = useAuth(); // Get auth context

  const handleChange = (field: keyof LoginFormData, value: string | boolean) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    if (errors[field as keyof LoginError]) {
      setErrors(prev => ({
        ...prev,
        [field]: undefined
      }));
    }
    
    if (loginError) {
      setLoginError(null);
    }
  };
  
  const togglePasswordVisibility = () => {
    setShowPassword(prev => !prev);
  };
  
  const validateForm = (): boolean => {
    const newErrors: LoginError = {};
    let isValid = true;
    
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
      isValid = false;
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
      isValid = false;
    }
    
    if (!formData.password) {
      newErrors.password = 'Password is required';
      isValid = false;
    }
    
    setErrors(newErrors);
    return isValid;
  };
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    try {
      setIsLoading(true);
      setLoginError(null); // Clear previous general errors
      
      // Simulate API call with timeout
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      // Mock login logic - replace with actual API call
      if (formData.email === 'test@mwm.com' && formData.password === 'password') {
        // ---- THIS IS WHERE YOU INTEGRATE AuthContext ----
        const mockUserData = { // Replace with actual user data from API
          id: 'vendor123',
          name: 'Test Vendor MWM',
          email: formData.email,
          // other vendor details...
        };
        const mockToken = 'fake-jwt-token-for-mwm-vendor'; // Replace with actual token from API

        auth.login(mockUserData, mockToken); // Call login from AuthContext
        
        // Navigate to dashboard (AuthContext's login can also handle this if preferred)
        navigate('/vendor/dashboard'); 
        // ---- END OF AuthContext INTEGRATION ----

      } else {
        setLoginError('Invalid email or password. Please try again.');
      }
    } catch (error) {
      // If your API call itself throws an error that you catch
      setLoginError('An error occurred during login. Please try again.');
      console.error('Login error:', error);
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleForgotPassword = () => {
    console.log('Forgot password clicked');
    // navigate('/vendor/forgot-password'); // Example navigation
  };
  
  const handleCreateAccount = () => {
    navigate('/vendor/register'); // Use navigate
  };
  
  return (
    <Container maxWidth="sm" sx={{ py: 8 }}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center'
        }}
      >
        <Box sx={{ mb: 4, textAlign: 'center' }}>
          <Typography variant="h4" component="h1" gutterBottom fontWeight="bold" color="primary">
            Vendor Portal
          </Typography>
          <Typography variant="subtitle1" color="textSecondary">
            Log in to manage your business
          </Typography>
        </Box>
        
        <Paper
          elevation={3}
          sx={{
            p: { xs: 2, sm: 4 }, // Responsive padding
            width: '100%',
            borderRadius: 2
          }}
        >
          {loginError && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {loginError}
            </Alert>
          )}
          
          <form onSubmit={handleSubmit}>
            <Box sx={{ mb: 3 }}>
              <TextField
                id="email"
                label="Email Address"
                variant="outlined"
                fullWidth
                value={formData.email}
                onChange={(e) => handleChange('email', e.target.value)}
                error={!!errors.email}
                helperText={errors.email}
                disabled={isLoading}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Email color="action" />
                    </InputAdornment>
                  ),
                }}
              />
            </Box>
            
            <Box sx={{ mb: 1 }}>
              <TextField
                id="password"
                label="Password"
                type={showPassword ? 'text' : 'password'}
                variant="outlined"
                fullWidth
                value={formData.password}
                onChange={(e) => handleChange('password', e.target.value)}
                error={!!errors.password}
                helperText={errors.password}
                disabled={isLoading}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Lock color="action" />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={togglePasswordVisibility}
                        edge="end"
                        disabled={isLoading}
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Box>
            
            <Box 
              sx={{ 
                display: 'flex', 
                justifyContent: 'space-between',
                alignItems: 'center',
                mb: 3,
                flexWrap: 'wrap',
                gap: 1 // Add gap for better spacing on wrap
              }}
            >
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.rememberMe}
                    onChange={(e) => handleChange('rememberMe', e.target.checked)}
                    disabled={isLoading}
                    color="primary"
                  />
                }
                label="Remember me"
                sx={{ mr: 'auto' }} // Push "Forgot password" to the right
              />
              
              <Link
                component="button"
                type="button"
                variant="body2"
                onClick={handleForgotPassword}
                underline="hover"
                disabled={isLoading}
                sx={{ cursor: 'pointer' }}
              >
                Forgot password?
              </Link>
            </Box>
            
            <Button
              type="submit"
              variant="contained"
              color="primary"
              fullWidth
              size="large"
              disabled={isLoading}
              sx={{ 
                py: 1.5,
                mb: 2,
                borderRadius: 1.5 
              }}
            >
              {isLoading ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                'Sign In'
              )}
            </Button>
          </form>
          
          <Divider sx={{ my: 3 }}>
            <Typography variant="body2" color="textSecondary">
              OR
            </Typography>
          </Divider>
          
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="body2" sx={{ mb: 1.5 }}>
              Don't have an account yet?
            </Typography>
            <Button
              variant="outlined"
              color="primary"
              onClick={handleCreateAccount}
              fullWidth
              sx={{ borderRadius: 1.5 }}
              disabled={isLoading}
            >
              Create Account
            </Button>
          </Box>
          
          <Box sx={{ mt: 3, textAlign: 'center' }}>
            <Typography variant="caption" color="textSecondary">
              By signing in, you agree to our 
              <Link href="/terms" underline="hover" sx={{ mx: 0.5 }}>
                Terms of Service
              </Link>
              and
              <Link href="/privacy" underline="hover" sx={{ ml: 0.5 }}>
                Privacy Policy
              </Link>
            </Typography>
          </Box>
        </Paper>
        
        <Box sx={{ mt: 4, textAlign: 'center' }}>
          <Typography variant="body2" color="textSecondary">
            Need help? 
            <Link href="/support" underline="hover" sx={{ ml: 0.5 }}>
              Contact Support
            </Link>
          </Typography>
        </Box>
      </Box>
    </Container>
  );
};

export default VendorLogin;